import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link as RouterLink } from "react-router-dom";
import {
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdClose,
} from "react-icons/md";
import zikoraLogo from "../images/ZikoraLogoWhite 1.svg";

const raleway = { fontFamily: "'Geist', sans-serif" };

const NAV_ITEMS = [
  {
    label: "Accounts",
    children: [
      { label: "Savings", to: "/saving-account" },
      { label: "Current", to: "/current-account" },
      { label: "Fixed Deposits", to: "/fixed-deposit" },
    ],
  },
  {
    label: "Loans",
    children: [
      { label: "Loans Overview", to: "/loans" },
      { label: "School Fees Loan", to: "/school-fees-loan" },
    ],
  },
  {
    label: "SME",
    children: [{ label: "Business Banking", to: "/business-banking" }],
  },
  {
    label: "Corporate",
    children: [
      {
        label: "Corporate Login",
        href: "https://zikoramfb-corporate.lucidbanking.com",
      },
    ],
  },
  {
    label: "E-Banking",
    children: [
      {
        label: "Online Banking",
        href: "https://zikoramfb.qoreonline.com/login",
      },
      { label: "Mobile Banking", to: "/mobile-banking" },
      { label: "Agency Banking", to: "/agency-banking" },
      { label: "USSD Banking", to: "/ussd-banking" },
    ],
  },
  {
    label: "About",
    children: [
      { label: "About Us", to: "/about" },
      { label: "Contact Us", to: "/contact" },
      { label: "Support", to: "/support" },
      { label: "Blog", to: "/blog" },
    ],
  },
];

const HamburgerIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 6h18M3 12h18M3 18h18"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const NavGroup = ({ label, children, onClose }) => {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          ...raleway,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 0",
          background: "none",
          border: "none",
          cursor: "pointer",
          fontWeight: 500,
          fontSize: "16px",
          lineHeight: "24px",
          color: "#DEDEDE",
        }}
      >
        {label}
        {open ? (
          <MdKeyboardArrowUp
            style={{ width: "20px", height: "20px", flexShrink: 0 }}
          />
        ) : (
          <MdKeyboardArrowDown
            style={{ width: "20px", height: "20px", flexShrink: 0 }}
          />
        )}
      </button>

      {open && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            paddingBottom: "12px",
          }}
        >
          {children.map((item) => {
            const linkStyle = {
              ...raleway,
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "24px",
              color: "#A3C4AD",
              textDecoration: "none",
              padding: "8px 0 8px 16px",
              display: "block",
            };
            if (item.href) {
              return (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                  onClick={onClose}
                >
                  {item.label}
                </a>
              );
            }
            return (
              <RouterLink
                key={item.label}
                to={item.to}
                style={linkStyle}
                onClick={onClose}
              >
                {item.label}
              </RouterLink>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MobileNav = () => {
  const [open, setOpen] = useState(false);

  const close = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const drawer = (
    <>
      {/* Backdrop */}
      {open && (
        <div
          onClick={close}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1100,
          }}
        />
      )}

      {/* Drawer */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "min(320px, 85vw)",
          height: "100dvh",
          backgroundColor: "#132A22",
          zIndex: 1200,
          display: "flex",
          flexDirection: "column",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.3s ease",
          overflowY: "auto",
          visibility: open ? "visible" : "hidden",
        }}
      >
        {/* Drawer header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 24px",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            flexShrink: 0,
          }}
        >
          <RouterLink to="/" onClick={close}>
            <img
              src={zikoraLogo}
              alt="Zikora MFB"
              style={{ width: "40px", height: "40px" }}
            />
          </RouterLink>
          <button
            onClick={close}
            aria-label="Close menu"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "4px",
            }}
          >
            <MdClose style={{ width: "24px", height: "24px" }} />
          </button>
        </div>

        {/* Nav items */}
        <nav style={{ padding: "0 24px", flex: 1 }}>
          {NAV_ITEMS.map((item) => (
            <NavGroup
              key={item.label}
              label={item.label}
              children={item.children}
              onClose={close}
            />
          ))}
        </nav>

        {/* CTA buttons */}
        <div
          style={{
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            borderTop: "1px solid rgba(255,255,255,0.08)",
            flexShrink: 0,
          }}
        >
          <a
            href="https://play.google.com/store/apps/details?id=com.clive.zikora"
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
            style={{
              ...raleway,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "48px",
              borderRadius: "8px",
              border: "1px solid #DEDEDE",
              fontWeight: 600,
              fontSize: "15px",
              color: "#FFFFFF",
              textDecoration: "none",
            }}
          >
            Download the App
          </a>

          <a
            href="https://zikoramfb.qoreonline.com/onboarding/type"
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
            style={{
              ...raleway,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "48px",
              borderRadius: "8px",
              backgroundColor: "#66A681",
              fontWeight: 600,
              fontSize: "15px",
              color: "#000000",
              textDecoration: "none",
            }}
          >
            Open Account
          </a>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Hamburger button */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <HamburgerIcon />
      </button>

      {typeof document !== "undefined" && createPortal(drawer, document.body)}
    </>
  );
};

export default MobileNav;
